import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaEnvelope, FaPhone, FaEdit, FaSave, FaTimes, FaGoogle, FaGithub } from 'react-icons/fa';
import '../styles/user-profile.css';

const UserProfilePage = () => {
  const { user, updateUser, logout } = useAuth();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    preferences: {
      emailNotifications: user?.preferences?.emailNotifications || true,
      storyReminders: user?.preferences?.storyReminders || true
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('preferences.')) {
      const prefKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [prefKey]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSave = () => {
    updateUser(formData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      bio: user?.bio || '',
      preferences: {
        emailNotifications: user?.preferences?.emailNotifications || true,
        storyReminders: user?.preferences?.storyReminders || true
      }
    });
    setIsEditing(false);
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case 'google':
        return <FaGoogle className="provider-icon google" />;
      case 'github':
        return <FaGithub className="provider-icon github" />;
      default:
        return <FaUser className="provider-icon default" />;
    }
  };

  if (!user) {
    return (
      <div className="user-profile-page">
        <div className="container">
          <div className="auth-required">
            <h1>{t('auth.signInRequiredTitle')}</h1>
            <p>{t('auth.signInRequiredMessage')}</p>
            <button onClick={() => window.location.href = '/'} className="btn-primary">
              {t('auth.goHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      <div className="container">
        <div className="profile-header">
          <div className="profile-avatar">
            <img src={user.avatar} alt={user.name} />
            {getProviderIcon(user.provider)}
          </div>
          <div className="profile-title">
            <h1>{t('userProfilePage.title')}</h1>
            <p>{t('userProfilePage.subtitle')}</p>
          </div>
          <div className="profile-actions">
            {!isEditing ? (
              <button onClick={() => setIsEditing(true)} className="btn-secondary">
                <FaEdit /> {t('userProfilePage.editProfile')}
              </button>
            ) : (
              <div className="edit-actions">
                <button onClick={handleSave} className="btn-primary">
                  <FaSave /> {t('userProfilePage.saveChanges')}
                </button>
                <button onClick={handleCancel} className="btn-secondary">
                  <FaTimes /> {t('userProfilePage.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="profile-content">
          <div className="profile-section">
            <h2>{t('userProfilePage.personalInfo')}</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>
                  <FaUser className="field-icon" />
                  {t('userProfilePage.fullName')}
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t('userProfilePage.fullNamePlaceholder')}
                  />
                ) : (
                  <div className="field-value">{user.name}</div>
                )}
              </div>

              <div className="form-group">
                <label>
                  <FaEnvelope className="field-icon" />
                  {t('userProfilePage.email')}
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('userProfilePage.emailPlaceholder')}
                  />
                ) : (
                  <div className="field-value">{user.email}</div>
                )}
              </div>

              <div className="form-group">
                <label>
                  <FaPhone className="field-icon" />
                  {t('userProfilePage.phone')}
                </label>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={t('userProfilePage.phonePlaceholder')}
                  />
                ) : (
                  <div className="field-value">{user.phone || t('userProfilePage.notProvided')}</div>
                )}
              </div>

              <div className="form-group full-width">
                <label>{t('userProfilePage.about')}</label>
                {isEditing ? (
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder={t('userProfilePage.aboutPlaceholder')}
                    rows="4"
                  />
                ) : (
                  <div className="field-value">{user.bio || t('userProfilePage.noBio')}</div>
                )}
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>{t('userProfilePage.preferences')}</h2>
            <div className="preferences-grid">
              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="preferences.emailNotifications"
                    checked={isEditing ? formData.preferences.emailNotifications : user.preferences?.emailNotifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                  <span className="checkmark"></span>
                  {t('userProfilePage.emailNotifications')}
                </label>
                <p>{t('userProfilePage.emailNotificationsDesc')}</p>
              </div>

              <div className="preference-item">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="preferences.storyReminders"
                    checked={isEditing ? formData.preferences.storyReminders : user.preferences?.storyReminders}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                  <span className="checkmark"></span>
                  {t('userProfilePage.storyReminders')}
                </label>
                <p>{t('userProfilePage.storyRemindersDesc')}</p>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h2>{t('userProfilePage.account')}</h2>
            <div className="account-info">
              <div className="account-item">
                <strong>{t('userProfilePage.accountId')}</strong> {user.id}
              </div>
              <div className="account-item">
                <strong>{t('userProfilePage.provider')}</strong> 
                <span className="provider-badge">
                  {getProviderIcon(user.provider)}
                  {user.provider?.charAt(0).toUpperCase() + user.provider?.slice(1)}
                </span>
              </div>
              <div className="account-item">
                <strong>{t('userProfilePage.memberSince')}</strong> {t('userProfilePage.demoAccount')}
              </div>
            </div>
            
            <div className="danger-zone">
              <h3>{t('userProfilePage.dangerZone')}</h3>
              <button onClick={logout} className="btn-danger">
                {t('userProfilePage.signOut')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfilePage; 